import React from "react";
import { useShallow } from "zustand/react/shallow";
import { RoomPlayerState } from "@uno/protocol";
import useAnimation from "hook/useAnimation";
import useClick from "hook/useClick";
import usePlayCard from "hook/room/usePlayCard";
import useStoreRoomPlayer from "store/useStoreRoomPlayer";
import useStoreRoomScene from "store/useStoreRoomScene";
import { CardProps } from "types/Card";
import { getCardContent } from "utils/CardContent";

const Card = React.memo(function Card(props: CardProps) {
    const { id, type, canBePut, isHoverable, style, positionInDiscardStack } = props;
    const ref = React.useRef<HTMLDivElement>(null);
    const discardRef = React.useRef<HTMLDivElement>();
    const roomPlayerState = useStoreRoomPlayer(useShallow(state => state.roomPlayer?.state));
    const hoveredCardId = useStoreRoomScene(useShallow(state => state.hoveredCardId));
    const setHoveredCardId = useStoreRoomScene(useShallow(state => state.setHoveredCardId));
    const playCard = usePlayCard();
    const isHovered = hoveredCardId === id;
    const isDraggable = React.useMemo(() => isHoverable && [RoomPlayerState.Active, RoomPlayerState.AwaitingAction].includes(roomPlayerState as RoomPlayerState), [isHoverable, roomPlayerState]);

    React.useEffect(() => {
        if(!isHoverable && isHovered) {
            setHoveredCardId(null);
        }
    }, [isHoverable, isHovered, setHoveredCardId]);

    const handleCardClick = React.useCallback(() => {
        if(!isDraggable || !isHoverable) {
            return;
        }

        if(!isHovered) {
            setHoveredCardId(id);
            return;
        }

        playCard(id);
    }, [isDraggable, isHoverable, isHovered, id, setHoveredCardId, playCard]);

    React.useEffect(() => {
        const discardContainer = document.querySelector("#discard-container") as HTMLDivElement;

        if(positionInDiscardStack) {
            ref.current?.setAttribute("data-position-in-discard-stack", positionInDiscardStack.toString());
        }

        if(discardContainer) {
            discardRef.current = discardContainer;
        }
    }, []);

    useAnimation({ id, ref });
    useClick(handleCardClick, ref);

    React.useEffect(() => {
        if(!isHovered) {
            return;
        }

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;

            if(ref.current?.contains(target)) {
                return;
            }

            const discardEl = document.getElementById("discard");

            if(discardEl?.contains(target)) {
                return;
            }

            setHoveredCardId(null);
        };

        document.addEventListener("click", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [isHovered, setHoveredCardId]);

    const content = getCardContent(type);

    const card =
        <div ref={ref} className={`card-container ${!canBePut && isHoverable ? "is-dimmed" : ""}`} data-card-id={id} style={style || {}}>
            <div className={`card-inner ${type.toLowerCase() === "back" ? "flat" : ""}`}>
                <div className={`card ${type.toLowerCase()} ${isHoverable ? "cursor-pointer duration-200 ease-in-out" : ""} ${isHovered ? "-translate-y-10" : ""}`}>
                    <div>{content.corner}</div>
                    <div>{content.corner}</div>
                    <div>{content.center}</div>
                    <span className="card-dim"></span>
                </div>

                {type.toLowerCase() !== "back" &&
                    <div className="card back" style={{ transform: "rotateY(180deg)" }}>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                }
            </div>
        </div>
    ;

    return card;
});

export default React.memo(Card);
